/**
 * Generated With: pnpm x packages/api/tools/generate-shopify-rest-model.ts
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCheckoutKey,
  ShopifyCheckoutLineItemKey,
  ShopifyCheckoutLineItemToCheckoutKey,
  ShopifyCheckoutLineItemToProductKey,
  ShopifyCheckoutLineItemToShopKey,
  ShopifyCheckoutLineItemToVariantKey,
  ShopifyProductKey,
  ShopifyProductVariantKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCheckoutLineItemKey,
  defaultFilterableApiIdentifiers: ["key"],
  "2022-01": {
    name: "Shopify Checkout Line Item",
    fields: [
      {
        name: "Applied Discounts",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "applied_discounts" } },
      },
      {
        name: "Compare At Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "compare_at_price" } },
      },
      {
        name: "Discount Allocations",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discount_allocations" } },
      },
      {
        name: "Fulfillment Service",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "fulfillment_service" } },
      },
      {
        name: "Grams",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "grams" } },
      },
      {
        name: "Key",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "key" } },
      },
      {
        name: "Line Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "line_price" } },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price" } },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutLineItemToProductKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "product_id" } },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity" } },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "requires_shipping" } },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "sku" } },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "taxable" } },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "title" } },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutLineItemToVariantKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_id" } },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_title" } },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "vendor" } },
      },
      {
        name: "Checkout",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutLineItemToCheckoutKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "checkout_id" } },
        configuration: {
          relatedModelKey: ShopifyCheckoutKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutLineItemToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "custom", resource: "checkout" }, // Special case, requires read_orders to list abandoned checkouts
  },
  "2025-04": {
    fields: [
      {
        name: "Applied Discounts",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "applied_discounts", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Compare At Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "compare_at_price", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Discount Allocations",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "discount_allocations", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Fulfillment Service",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "fulfillment_service", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Grams",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "grams", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Key",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "key", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Line Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "line_price", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "price", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutLineItemToProductKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "product_id", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "quantity", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "requires_shipping", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "sku", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "taxable", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "title", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutLineItemToVariantKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "variant_id", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "variant_title", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "vendor", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
    ],
    syncDisabled: true,
    syncConfig: {
      type: "webhook_only",
    },
  },
};

export const ShopifyCheckoutLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
